import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Domain } from "~/api/Api.ts";
import { apiClient } from "~/api/ApiClient.ts";
import { Icon } from "~/assets/icons/Icon.tsx";
import Button from "~/components/mantine/button/Button.tsx";
import Select from "~/components/mantine/select/Select.tsx";
import { useSites } from "~/components/siteSpeed/hooks.ts";
import { useVisibilityEngineRoutes } from "~/components/siteSpeed/useVisibilityEngineRoutes";
import ButtonWithSpinner from "~/components/ui/button/Button.tsx";
import { AlertCard } from "~/components/ui/card/AlertCard";
import CopyButton from "~/components/ui/copyButton/CopyButton.tsx";
import Input from "~/components/ui/input/Input.tsx";
import Spinner from "~/components/ui/spinner/Spinner.tsx";
import { useWizard } from "~/contexts/WizardContext";

import styles from "./Wizzard.module.scss";

type NameserversProps = {
  isWizzard?: boolean;
  domain?: Domain | undefined;
};

const CLOUDFLARE_DASH_URL = "https://dash.cloudflare.com";

const WORKER_CODE = `
export default {
  async fetch(request, env, ctx) {
    // Access token from the AI Visibility Engine setup. Protects from abuse and unauthorized access.
    const accessToken = "***";

    // Only intercept GET requests
    if (request.method !== "GET") return fetch(request);
    
    // Skip proxied requests to prevent recursion
    if (request.headers.get("Alli-Token") == accessToken) return fetch(request);

    // Detect bot traffic using User-Agent
    const ua = (request.headers.get("User-Agent") || "").toLowerCase();
    
    const isBot =
      ua.includes("bot") ||
      ua.includes("crawler") ||
      ua.includes("spider") ||
      ua.includes("slurp");

    if (!isBot) return fetch(request);

    const downstreamURL = new URL(request.url);
    
    // Clone headers and preserve original Host
    const headers = new Headers(request.headers);

    // Strip untrusted inbound headers
    headers.delete("Alli-Host");
    headers.delete("Alli-Token");

    // Add the origin Host and the token
    headers.set("Alli-Host", downstreamURL.hostname);
    headers.set("Alli-Token", accessToken);

    const upstreamURL = new URL(request.url);
    
    // Rewrite destination to the AI Visibility Engine
    upstreamURL.hostname = "engine.alliaiapp.com";

    const proxyRequest = new Request(upstreamURL.toString(), {
      method: "GET",
      headers,
      redirect: "manual",
    });

    return fetch(proxyRequest);
  },
};`;

export const Confirmation = ({ isWizzard = false }: NameserversProps) => {
  const [siteId, setSiteId] = useState<number>();
  const [isChecking, setIsChecking] = useState(false);
  const { domainId } = useParams<{ domainId: string }>();
  const navigate = useNavigate();
  const veRoutes = useVisibilityEngineRoutes();
  const { navigateToPrevStep } = useWizard();

  const { data: sitesData, isPending: isPendingSitesData } = useSites(
    Number(domainId),
  );

  const { data: domainStatus } = useQuery({
    enabled: isChecking,
    queryKey: ["domainStatus", domainId, "confirmation"],
    queryFn: async () => {
      const response = await apiClient.domains.statusDetail(Number(domainId));

      return response?.data?.domain;
    },
    refetchOnWindowFocus: false,
    refetchInterval: isChecking ? 10000 : false,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (isChecking && domainStatus?.ns_set && domainId) {
      setIsChecking(false);
      navigate(veRoutes.domain(domainId));
    }
  }, [isChecking, domainStatus, domainId, navigate, veRoutes]);

  const handleCheckInstallation = () => {
    setIsChecking(true);
  };

  const sites =
    sitesData?.sites &&
    sitesData.sites.filter((site) => site.prerendering_status === "enabled");

  const site = sites?.find((s) => s.id === siteId);
  const token = site?.proxy_token ?? "";

  const handleSiteSelect = (id: number) => {
    setSiteId(id);
  };

  // set site automatically if there is only one
  useEffect(() => {
    if (sites?.length === 1) {
      setSiteId(sites[0].id);
    }
  }, [sites]);

  if (isPendingSitesData) {
    return <Spinner />;
  }

  return (
    // @ts-expect-error TS2339 non-existing className
    <div className={styles.stepContent}>
      <div>
        <div className={styles.stepContentTitle}>
          Edge Integration Instructions
        </div>
        <div>
          Deploy a Cloudflare Worker to integrate AI Visibility Engine with your
          site.
        </div>

        {sites && sites.length > 0 ? (
          <>
            {sites.length > 1 && (
              <div className={styles.sitesSelector}>
                <Select
                  data={sites.map((s) => ({
                    label: s.name,
                    value: String(s.id),
                  }))}
                  placeholder="Select Site"
                  isLoading={false}
                  onChange={(id) => handleSiteSelect(Number(id))}
                />
              </div>
            )}

            {siteId && (
              <>
                <AlertCard
                  neutralHeader={false}
                  className={styles.accessTokenInfoBlock}
                  type="info"
                  title="Your Access Token"
                  content={
                    <div>
                      <div>
                        This token authenticates requests between your Worker
                        and the AI Visibility Engine. Keep it secure and never
                        expose it publicly.
                      </div>
                      <div
                        className={clsx(
                          styles.stepContentCodeInput,
                          styles.accessTokenValue,
                        )}
                      >
                        <Input
                          readOnly
                          isSecret
                          value={token}
                          ownProps={{ noErrors: true }}
                          wrapperClassName={styles.fullWidthInput}
                        />
                        <CopyButton value={token} />
                      </div>
                    </div>
                  }
                />

                <div className={styles.edgeInstallationDetailsWrapper}>
                  <div className={styles.subTitle}>Installation Steps</div>
                  <div className={styles.edgeInstallationDetailsContent}>
                    <div className={styles.edgeInstallationDetailsStep}>
                      <div className={styles.stepNumber}>1</div>
                      <div
                        className={styles.edgeInstallationDetailsStepContent}
                      >
                        <strong>Sign in to Cloudflare Dashboard</strong>
                        <div
                          className={
                            styles.edgeInstallationDetailsStepContentDescription
                          }
                        >
                          Go to{" "}
                          <a
                            href={CLOUDFLARE_DASH_URL}
                            className={styles.externalLink}
                            target="_blank"
                          >
                            dash.cloudflare.com
                            <Icon iconName="ExternalLinkIcon" />
                          </a>
                          and navigate to Workers & Pages.
                        </div>
                      </div>
                    </div>

                    <div className={styles.edgeInstallationDetailsStep}>
                      <div className={styles.stepNumber}>2</div>
                      <div
                        className={styles.edgeInstallationDetailsStepContent}
                      >
                        <strong>Create a new Worker</strong>
                        <div
                          className={
                            styles.edgeInstallationDetailsStepContentDescription
                          }
                        >
                          Click "Create application" → "Create Worker" → Give it
                          a name (e.g., "visibility-engine")
                        </div>
                      </div>
                    </div>

                    <div className={styles.edgeInstallationDetailsStep}>
                      <div className={styles.stepNumber}>3</div>
                      <div
                        className={styles.edgeInstallationDetailsStepContent}
                      >
                        <strong>Copy and paste the Worker code</strong>
                        <div
                          className={
                            styles.edgeInstallationDetailsStepContentDescription
                          }
                        >
                          Replace the default code with the code below. Your
                          access token is already included.
                          <div className={styles.codeBlock}>
                            <CopyButton
                              value={WORKER_CODE}
                              className={styles.codeCopyButton}
                            />
                            <pre>
                              <code>{WORKER_CODE}</code>
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.edgeInstallationDetailsStep}>
                      <div className={styles.stepNumber}>4</div>
                      <div
                        className={styles.edgeInstallationDetailsStepContent}
                      >
                        <strong>Deploy the Worker</strong>
                        <div
                          className={
                            styles.edgeInstallationDetailsStepContentDescription
                          }
                        >
                          Click "Save and Deploy" to publish your Worker.
                        </div>
                      </div>
                    </div>

                    <div className={styles.edgeInstallationDetailsStep}>
                      <div className={styles.stepNumber}>5</div>
                      <div
                        className={styles.edgeInstallationDetailsStepContent}
                      >
                        <strong>Add a route for your domain</strong>
                        <div
                          className={
                            styles.edgeInstallationDetailsStepContentDescription
                          }
                        >
                          In the Worker settings, go to "Triggers" → "Add Route"
                          → Enter foo.com/* and select your zone.
                        </div>
                      </div>
                    </div>

                    <div className={styles.edgeInstallationDetailsStep}>
                      <div className={styles.stepNumber}>6</div>
                      <div
                        className={styles.edgeInstallationDetailsStepContent}
                      >
                        <strong>Test the integration</strong>
                        <div
                          className={
                            styles.edgeInstallationDetailsStepContentDescription
                          }
                        >
                          Visit your site using a bot User-Agent to verify the
                          Worker is routing traffic to the AI Visibility Engine.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.edgeInstallationDetailsWrapper}>
                  <div className={styles.subTitle}>How it works</div>
                  <div className={styles.edgeInstallationDetailsContent}>
                    <ul className={styles.edgeInstallationDetailsList}>
                      <li>
                        The Worker intercepts GET requests from search engine
                        bots (detected via User-Agent)
                      </li>
                      <li>
                        Bot requests are forwarded to the AI Visibility Engine
                        for optimization
                      </li>
                      <li>
                        Regular user traffic continues to your origin server
                        unchanged
                      </li>
                      <li>
                        No DNS changes required – everything works at the edge
                      </li>
                    </ul>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className={styles.edgeInstallationNoSites}>
            You have no sites with enabled prerendering. Go to AI Visibility
            Engine Status section and enable one.
          </div>
        )}
      </div>
      {isWizzard && (
        <div className={styles.stepContentFooter}>
          <Button variant="outlined" onClick={() => navigateToPrevStep()}>
            Back
          </Button>
          <ButtonWithSpinner
            type="button"
            onClick={handleCheckInstallation}
            disabled={isChecking}
            withSpinner={isChecking}
          >
            {isChecking ? "Waiting for Requests" : "Check Installation"}
          </ButtonWithSpinner>
        </div>
      )}
    </div>
  );
};
